<template>
  <div class="line fixed-fields">
    
    <div v-if="loading" class="loading-container">
      <p>Chargement des champs...</p>
    </div>
    
    <div v-else>
      <div class="form-row">
        <div 
          v-for="field in generalFields" 
          :key="field.value"
          v-show="shouldShowField(field.value)"
          class="form-group"
          :class="{ 'full-width': isFullWidthField(field.value) }"
        >
          <label :for="field.value">{{ field.label }}</label>
          
          <!-- Select si le champ a des valeurs disponibles -->
          <select
            v-if="field.availableValues"
            :id="field.value"
            :value="modelValue[getCamelCaseKey(field.value)]"
            @change="updateField(getCamelCaseKey(field.value), $event.target.value)"
          >
            <option value="">-- Sélectionner --</option>
            <option 
              v-for="(valueLabel, valueKey) in field.availableValues" 
              :key="valueKey" 
              :value="valueKey"
            >
              {{ valueLabel }}
            </option>
          </select>
          
          <!-- Textarea pour les commentaires -->
          <textarea
            v-else-if="isTextareaField(field.value)"
            :id="field.value"
            :value="modelValue[getCamelCaseKey(field.value)]"
            @input="updateField(getCamelCaseKey(field.value), $event.target.value)"
            :placeholder="field.label + '...'"
            rows="2"
          ></textarea>
          
          <!-- Input text par défaut -->
          <input
            v-else
            type="text"
            :id="field.value"
            :value="modelValue[getCamelCaseKey(field.value)]"
            @input="updateField(getCamelCaseKey(field.value), $event.target.value)"
            :placeholder="field.label + '...'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getWorkFieldsWithValues } from '@/utils/preChiffrageHelpers'

export default {
  name: 'FormGeneralFields',
  
  props: {
    modelValue: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  
  emits: ['update:modelValue'],
  
  data() {
    return {
      loading: true,
      generalFields: []
    }
  },
  
  methods: {
    /**
     * Convertit une clé slug en camelCase
     * Ex: 'sinistre-oui' -> 'sinistreOui'
     */
    getCamelCaseKey(slugKey) {
      return slugKey.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
    },
    
    /**
     * Détermine si un champ doit être affiché
     * Les champs sinistre sont maintenant dans les lignes CAT et GPR
     */
    shouldShowField(fieldValue) {
      // Masquer les champs liés au sinistre car ils sont maintenant dans chaque ligne CAT/GPR
      if (fieldValue === 'sinistre-oui' || fieldValue === 'commentaires-sinistre') {
        return false
      }
      
      // Tous les autres champs sont affichés
      return true
    },
    
    /**
     * Détermine si un champ doit être un textarea
     */
    isTextareaField(fieldValue) {
      return fieldValue.includes('commentaires')
    },
    
    /**
     * Détermine si un champ doit occuper toute la largeur
     */
    isFullWidthField(fieldValue) {
      return fieldValue.includes('commentaires')
    },
    
    /**
     * Met à jour un champ dans le modèle
     */
    updateField(key, value) {
      this.$emit('update:modelValue', {
        ...this.modelValue,
        [key]: value
      })
    },
    
    /**
     * Charge les champs généraux depuis le dictionnaire
     * Les champs généraux sont ceux qui n'ont pas de workType associé (null)
     */
    async loadGeneralFields() {
      try {
        const allFields = await getWorkFieldsWithValues()
        
        // Filtrer les champs généraux (ceux sans workType)
        this.generalFields = allFields.filter(field => field.workType === null)
        
      } catch (error) {
        console.error('Erreur lors du chargement des champs généraux:', error)
        this.generalFields = []
      } finally {
        this.loading = false
      }
    }
  },
  
  async mounted() {
    await this.loadGeneralFields()
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/variables.scss';

.fixed-fields {
  padding: 0.5rem 1rem 1rem;
  border-radius: $radius;
  background-color: $white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
  margin-bottom: $size-4;
  align-items: center;
}

.loading-container {
  padding: 1rem;
  text-align: center;
  color: #666;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  
  .form-group.full-width {
    grid-column: 1 / -1;
  }
}

.form-group {
  margin-bottom: 0.5rem;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 0.3rem;
    color: #333;
    font-size: 0.875rem;
  }

  input[type="number"],
  input[type="text"],
  select,
  textarea {
    width: 100%;
    padding: 0.5rem 0.6rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    font-family: inherit;
    background: white;
    
    &:focus {
      outline: none;
      border-color: #0066cc;
      box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.1);
    }
  }

  select {
    cursor: pointer;
  }

  textarea {
    /* Élastique : grandit avec le contenu */
    field-sizing: content;
    min-height: 60px;
    max-height: 300px;
    overflow-y: auto;
    resize: none;
  }
}
</style>
